import { forecastMath } from 'features/math'
import { ChartDataPoint } from 'features/systemLens'
import {
  computedPenId,
  type ComputedTrendSeries,
} from 'features/timeSeries/computed/computedTrendStore'
import { formatLocalChartTime } from 'features/timeSeries/utils/mergeTimeSeriesResponse'
import { smoothedColumnId } from 'features/timeSeries/utils/penCompanions'
import {
  BaseSensorEntry,
  SmoothingConfig,
  ThresholdFilterConfig,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

const { evaluate } = forecastMath

/**
 * A sensor id as a math.js identifier.
 *
 * Dashes and periods read as operators, so they go. The leading underscore is
 * the part that is not obvious: sites that key datapoints by bare UUID produce
 * ids like `07c14370-472d-4671-939f-1c1100351358`, and `07c14370_472d_...` is
 * tokenised as the number `07` followed by an undefined symbol. The expression
 * throws, the derived value yields nothing, and the pen draws as a flat empty
 * line — indistinguishable from a model that found no signal. Ten of the sixteen
 * hex digits are numerals, so most UUID-keyed tags were affected; ids beginning
 * with a letter, which is every tag at sites that prefix them, are unchanged.
 */
export const sanitizeSensorId = (id: string): string => {
  const replaced = id.replace(/-/g, '_').replace(/\./g, '_')
  return /^[0-9]/.test(replaced) ? `_${replaced}` : replaced
}

// Get sensor name helper
export const getSensorName = (
  sensorId: string,
  derivedValues: TimeSeriesDerivedValueEntryJSON[],
  sensorEntries: BaseSensorEntry[]
) => {
  // Check if it's a derived value first
  if (sensorId.startsWith('derived_')) {
    const derivedValue = derivedValues.find((dv) => dv.id === sensorId)
    if (derivedValue) {
      return derivedValue.data.name
    }
  }

  // Otherwise look for a sensor
  const sensor = sensorEntries.find((e) => e.id === sensorId)
  return sensor?.data.name || sensorId
}

/**
 * Get all items to render (sensors + derived values with dependencies met)
 */
export const getAllItemsToRender = (
  selectedSensors: Array<{
    id: string
    name?: string
    description?: string
    unit?: string
  }>,
  derivedValues: TimeSeriesDerivedValueEntryJSON[],
  sensorEntries: BaseSensorEntry[]
): Array<{
  id: string
  name: string
  isDerived: boolean
  description?: string
  unit?: string
}> => {
  const items: Array<{
    id: string
    name: string
    isDerived: boolean
    description?: string
    unit?: string
  }> = selectedSensors.map((sensor) => ({
    id: sensor.id,
    name: getSensorName(sensor.id, derivedValues, sensorEntries),
    isDerived: false,
    description: sensor.description,
    unit: sensor.unit,
  }))

  // Add derived values that have all dependencies selected
  derivedValues.forEach((derived) => {
    const allDependenciesSelected = derived.data.dependencyIds.every(
      (sensorId) => selectedSensors.some((selected) => selected.id === sensorId)
    )

    if (allDependenciesSelected) {
      items.push({
        id: derived.id,
        name: derived.data.name,
        isDerived: true,
        description: `Derived: ${derived.data.name}`,
        unit: derived.data.unit || '',
      })
    }
  })

  return items
}

const enabledFilters = (
  filters: Record<string, ThresholdFilterConfig>
): ThresholdFilterConfig[] => Object.values(filters).filter((f) => f.enabled)

const buildStabilityCV = (
  data: ChartDataPoint[],
  filter: ThresholdFilterConfig
): Map<number, number> => {
  const sensorCVs = new Map<number, number>()
  const windowSize = filter.value2 || 10

  data.forEach((_, index) => {
    const windowStart = Math.max(0, index - Math.floor(windowSize / 2))
    const windowEnd = Math.min(data.length, index + Math.ceil(windowSize / 2))

    const windowValues: number[] = []
    for (let i = windowStart; i < windowEnd; i++) {
      const value = data[i][filter.sensorId]
      if (typeof value === 'number') {
        windowValues.push(value)
      }
    }

    if (windowValues.length < 2) return

    const mean = windowValues.reduce((a, b) => a + b, 0) / windowValues.length
    if (mean === 0) return

    const variance =
      windowValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
      windowValues.length
    sensorCVs.set(index, (Math.sqrt(variance) / Math.abs(mean)) * 100)
  })

  return sensorCVs
}

const buildCVCache = (
  data: ChartDataPoint[],
  activeFilters: ThresholdFilterConfig[]
): Map<string, Map<number, number>> => {
  const cvCache = new Map<string, Map<number, number>>()
  activeFilters
    .filter((f) => f.condition === 'stability')
    .forEach((filter) => {
      cvCache.set(filter.sensorId, buildStabilityCV(data, filter))
    })
  return cvCache
}

const passesFilter = (
  value: number,
  filter: ThresholdFilterConfig,
  cv: number | undefined
): boolean => {
  switch (filter.condition) {
    case 'above':
      return value > filter.value1
    case 'below':
      return value < filter.value1
    case 'between':
      return (
        filter.value2 !== null &&
        filter.value2 !== undefined &&
        value >= filter.value1 &&
        value <= filter.value2
      )
    case 'stability':
      return cv !== undefined && cv <= filter.value1
    default:
      return true
  }
}

/**
 * Apply threshold filters, one pen at a time.
 *
 * This used to drop whole rows, which deleted the timestamp for every other pen
 * on the chart. A filter now blanks only its own pen's value and leaves the row
 * standing; `collectFilteredPoints` marks what was blanked.
 */
export const applyThresholdFilters = (
  data: ChartDataPoint[],
  filters: Record<string, ThresholdFilterConfig>
): ChartDataPoint[] => {
  if (!data.length) return data

  const activeFilters = enabledFilters(filters)
  if (!activeFilters.length) return data

  const cvCache = buildCVCache(data, activeFilters)

  return data.map((point, index) => {
    let next: ChartDataPoint | null = null

    activeFilters.forEach((filter) => {
      const value = point[filter.sensorId]
      // A missing sample is not an exclusion
      if (typeof value !== 'number') return
      if (passesFilter(value, filter, cvCache.get(filter.sensorId)?.get(index)))
        return

      next = next ?? { ...point }
      next[filter.sensorId] = null
    })

    return next ?? point
  })
}

/** A pen's excluded points, ready for the removed-point marker plugin. */
export interface FilteredPointsSeries {
  sensorId: string
  /** Epoch-ms positions, ascending. */
  xs: number[]
  values: (number | null)[]
}

/**
 * The points `applyThresholdFilters` blanks, with their original values so the
 * chart can mark them. Must be given the pre-filter rows.
 */
export const collectFilteredPoints = (
  data: ChartDataPoint[],
  filters: Record<string, ThresholdFilterConfig>
): FilteredPointsSeries[] => {
  if (!data.length) return []

  const activeFilters = enabledFilters(filters)
  if (!activeFilters.length) return []

  const cvCache = buildCVCache(data, activeFilters)

  return activeFilters.flatMap((filter) => {
    const cvs = cvCache.get(filter.sensorId)
    const xs: number[] = []
    const values: (number | null)[] = []

    data.forEach((point, index) => {
      const value = point[filter.sensorId]
      if (typeof value !== 'number') return
      if (passesFilter(value, filter, cvs?.get(index))) return
      xs.push(point.timestamp)
      values.push(value)
    })

    return xs.length ? [{ sensorId: filter.sensorId, xs, values }] : []
  })
}

interface ResolvedSmoothing {
  sensorId: string
  method: SmoothingConfig['method']
  basis: 'samples' | 'duration'
  windowSize: number
  windowSeconds: number
  alignment: 'trailing' | 'trailingShifted' | 'centered'
  display: 'overlay' | 'replace'
  requireFullWindow: boolean
}

// Defaults are the legacy behaviour, for configs saved before these fields
// existed: a centered window that overwrites the pen.
const resolveSmoothing = (config: SmoothingConfig): ResolvedSmoothing => ({
  sensorId: config.sensorId,
  method: config.method,
  basis: config.basis ?? 'samples',
  windowSize: config.windowSize,
  windowSeconds: config.windowSeconds ?? 0,
  alignment: config.alignment ?? 'centered',
  display: config.display ?? 'replace',
  requireFullWindow: config.requireFullWindow ?? false,
})

interface PenSample {
  rowIndex: number
  timestamp: number
  value: number
}

// A pen's own observations, in time order. Windows are counted in these rather
// than in merged rows, which on a multi-cadence chart hold other pens' samples.
const collectPenSamples = (
  data: ChartDataPoint[],
  sensorId: string
): PenSample[] => {
  const samples: PenSample[] = []
  data.forEach((point, rowIndex) => {
    const value = point[sensorId]
    if (typeof value !== 'number') return
    samples.push({ rowIndex, timestamp: point.timestamp, value })
  })
  return samples
}

const aggregateWindow = (
  values: number[],
  method: SmoothingConfig['method']
): number => {
  switch (method) {
    case 'avg':
      return values.reduce((a, b) => a + b, 0) / values.length
    case 'max':
      return Math.max(...values)
    case 'min':
      return Math.min(...values)
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b)
      const mid = Math.floor(sorted.length / 2)
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid]
    }
    default:
      return values[0]
  }
}

/** First sample position at or after `from`. */
const lowerBound = (samples: PenSample[], from: number): number => {
  let low = 0
  let high = samples.length
  while (low < high) {
    const mid = (low + high) >> 1
    if (samples[mid].timestamp < from) low = mid + 1
    else high = mid
  }
  return low
}

/**
 * Sample positions contributing to the value at `position`, as
 * `[start, endExclusive)`, or null when the window cannot be formed.
 */
const windowBounds = (
  samples: PenSample[],
  position: number,
  resolved: ResolvedSmoothing
): [number, number] | null => {
  const { basis, windowSize, windowSeconds, alignment, requireFullWindow } =
    resolved

  // trailingShifted ends one sample early so the current value never enters
  // its own window
  const anchor = alignment === 'trailingShifted' ? position - 1 : position
  if (anchor < 0) return null

  if (basis === 'duration') {
    if (windowSeconds <= 0) return null
    const anchorTime = samples[anchor].timestamp
    if (alignment === 'centered') {
      const half = (windowSeconds * 1000) / 2
      const start = lowerBound(samples, anchorTime - half)
      const end = lowerBound(samples, anchorTime + half + 1)
      return end > start ? [start, end] : null
    }
    const start = lowerBound(samples, anchorTime - windowSeconds * 1000)
    return [start, anchor + 1]
  }

  if (alignment === 'centered') {
    const start = Math.max(0, anchor - Math.floor(windowSize / 2))
    const end = Math.min(samples.length, anchor + Math.ceil(windowSize / 2))
    return end > start ? [start, end] : null
  }

  const start = anchor - windowSize + 1
  if (start < 0) return requireFullWindow ? null : [0, anchor + 1]
  return [start, anchor + 1]
}

/**
 * Apply smoothing configurations to chart data.
 *
 * With `display: 'overlay'` the result lands in a companion column so the raw
 * trace stays on screen next to it; with `replace` it overwrites the pen, which
 * is what the original smoother did.
 */
export const applySmoothingConfigs = (
  data: ChartDataPoint[],
  configs: Record<string, SmoothingConfig>
): ChartDataPoint[] => {
  if (!data.length) return data

  const activeConfigs = Object.values(configs).filter((c) => c.enabled)
  if (!activeConfigs.length) return data

  const smoothedData = data.map((point) => ({ ...point }))

  activeConfigs.forEach((config) => {
    const resolved = resolveSmoothing(config)
    const samples = collectPenSamples(data, resolved.sensorId)
    if (samples.length === 0) return

    const targetKey =
      resolved.display === 'overlay'
        ? smoothedColumnId(resolved.sensorId)
        : resolved.sensorId

    samples.forEach((sample, position) => {
      const bounds = windowBounds(samples, position, resolved)
      if (bounds === null) return

      const [start, end] = bounds
      const windowValues: number[] = []
      for (let i = start; i < end; i++) windowValues.push(samples[i].value)
      if (windowValues.length === 0) return
      if (
        resolved.requireFullWindow &&
        resolved.basis === 'samples' &&
        windowValues.length < resolved.windowSize
      ) {
        return
      }

      smoothedData[sample.rowIndex][targetKey] = aggregateWindow(
        windowValues,
        resolved.method
      )
    })
  })

  return smoothedData
}

/**
 * Evaluates each derived value on every row where one of its inputs has a
 * sample, using each input's latest sample at or before that row. Pens fetched
 * raw keep their own sample times, so inputs rarely share a row. A null marks
 * a gap, and the input is not carried across it. Rows must be ascending.
 */
export const applyDerivedValues = (
  data: ChartDataPoint[],
  derivedVals: TimeSeriesDerivedValueEntryJSON[]
): ChartDataPoint[] => {
  if (!data.length || !derivedVals.length) return data

  const latest: Record<string, number> = {}
  return data.map((point) => {
    const newPoint = { ...point }
    derivedVals.forEach((derived) => {
      if (!derived.data.expression) return

      let sampledHere = false
      derived.data.dependencyIds.forEach((sensorId) => {
        const value = newPoint[sensorId]
        if (typeof value === 'number') {
          latest[sensorId] = value
          sampledHere = true
        } else if (value === null) {
          delete latest[sensorId]
        }
      })
      if (!sampledHere) return

      const context: Record<string, number> = {}
      for (const sensorId of derived.data.dependencyIds) {
        const value = latest[sensorId]
        if (value === undefined) return
        // Shared with anything that GENERATES these expressions: the scope key
        // and the reference in the formula have to be the same string.
        context[sanitizeSensorId(sensorId)] = value
      }

      try {
        if ('timestamp' in context) {
          context.timestamp -= data[0].timestamp
        }
        const result = evaluate(derived.data.expression, context)

        if (typeof result === 'number' && isFinite(result)) {
          newPoint[derived.id] = result
        }
      } catch (error) {
        console.error(
          `Error evaluating expression "${derived.data.expression}":`,
          error,
          context
        )
      }
    })
    return newPoint
  })
}

/**
 * Reduces a series to roughly `maxPoints` for rendering, keeping the extremes.
 *
 * This previously strided the series — taking every Nth point and discarding
 * everything between — which silently deleted any peak that did not happen to
 * land on a stride index. Measured against ground truth on real tags, that
 * turned a 13.3% trough error into 34.6%: the deepest 30-day dip on a boiler
 * feedwater vibration pen rendered at 65% of its true depth. Operators read
 * these charts for excursions, so deleting the excursions is the one thing
 * downsampling must not do.
 *
 * Instead each window contributes its minimum and its maximum, emitted in time
 * order. The result is still a single line and still bounded by `maxPoints`,
 * but every high and low in the source survives to the screen.
 *
 * `maxPoints` is a target, not a hard cap: the first and last points are always
 * kept so the series keeps its true extent, which can exceed it by two.
 */
export const resampleData = <T>(
  xValues: T[],
  yValues: (number | null)[],
  maxPoints: number = 1000
): { x: T[]; y: (number | null)[] } => {
  if (xValues.length <= maxPoints) {
    return { x: xValues, y: yValues }
  }

  const resampledX: T[] = []
  const resampledY: (number | null)[] = []

  const push = (index: number) => {
    resampledX.push(xValues[index])
    resampledY.push(yValues[index])
  }

  // Each window yields up to two points, so budget against half the allowance.
  const windowCount = Math.max(1, Math.floor(maxPoints / 2))
  const windowSize = Math.ceil(xValues.length / windowCount)

  push(0)

  const lastIndex = xValues.length - 1
  for (let start = 1; start < lastIndex; start += windowSize) {
    const end = Math.min(start + windowSize, lastIndex)

    let minIndex = -1
    let maxIndex = -1
    let minValue = Infinity
    let maxValue = -Infinity

    for (let i = start; i < end; i += 1) {
      const value = yValues[i]
      if (value === null || !Number.isFinite(value)) continue
      if (value < minValue) {
        minValue = value
        minIndex = i
      }
      if (value > maxValue) {
        maxValue = value
        maxIndex = i
      }
    }

    // A window with no finite value is a real gap (gap_fill emits nulls for
    // empty buckets); keep one so the line still breaks instead of closing over
    // data that does not exist.
    if (minIndex === -1) {
      if (end > start) push(start)
      continue
    }

    // Ascending x is a uPlot requirement, so emit whichever extreme came first.
    if (minIndex === maxIndex) {
      push(minIndex)
    } else if (minIndex < maxIndex) {
      push(minIndex)
      push(maxIndex)
    } else {
      push(maxIndex)
      push(minIndex)
    }
  }

  if (lastIndex > 0) {
    push(lastIndex)
  }

  return { x: resampledX, y: resampledY }
}

/**
 * Union-merge computed series into the chart rows on their own time grid;
 * null values stay null so gated-out intervals render as gaps.
 */
export const mergeComputedSeriesRows = (
  rows: ChartDataPoint[],
  computedSeries: ComputedTrendSeries[],
  window?: { fromMs: number; toMs: number }
): ChartDataPoint[] => {
  if (!computedSeries.length) return rows

  const byTimestamp = new Map<number, ChartDataPoint>()
  rows.forEach((row) => byTimestamp.set(row.timestamp, row))

  const touched = new Set<number>()
  let addedRow = false

  computedSeries.forEach((series) => {
    const columnId = computedPenId(series.entryId)
    series.points.forEach(([timeIso, value]) => {
      const timestamp = new Date(timeIso).getTime()
      if (Number.isNaN(timestamp)) return
      if (window && (timestamp < window.fromMs || timestamp > window.toMs)) {
        return
      }
      let row = byTimestamp.get(timestamp)
      if (row === undefined) {
        row = { timestamp, time: formatLocalChartTime(timestamp) }
        byTimestamp.set(timestamp, row)
        addedRow = true
      } else if (!touched.has(timestamp)) {
        row = { ...row }
        byTimestamp.set(timestamp, row)
      }
      touched.add(timestamp)
      row[columnId] = value
    })
  })

  const merged = Array.from(byTimestamp.values())
  return addedRow
    ? merged.sort((left, right) => left.timestamp - right.timestamp)
    : merged
}
